import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { googleAvailabilityKeys, googleConnectionKeys } from "@/lib/query";

export interface GoogleConnectionStatus {
  connected: boolean;
  email: string | null;
}

/**
 * Status of the shared Google account connection (EPIC-013), for the admin
 * "Connect Google Account" card in Settings. Admin-gated server-side —
 * see /api/admin/google/status.
 */
export function useGoogleConnectionStatus() {
  return useQuery({
    queryKey: googleConnectionKeys.status,
    queryFn: async (): Promise<GoogleConnectionStatus> => {
      const res = await fetch("/api/admin/google/status");
      if (!res.ok) throw new Error("Failed to load Google connection status");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Any-signed-in-user check for whether the shared Google account is
 * connected — backs whether row actions like "Send calendar invite" are
 * shown. Deliberately separate from useGoogleConnectionStatus, which hits
 * the admin-gated /api/admin/google/status and would 403 for non-admins.
 */
export function useGoogleAvailable() {
  return useQuery({
    queryKey: googleAvailabilityKeys.connected,
    queryFn: async (): Promise<boolean> => {
      const res = await fetch("/api/google/status");
      if (!res.ok) throw new Error("Failed to load Google availability");
      const json = await res.json();
      return json.data.connected as boolean;
    },
    staleTime: 60_000,
  });
}

/**
 * Disconnects the shared Google account (deletes every stored `account` row
 * for the provider — see src/index.ts and .claude/rules/google-calendar.md).
 * Breaks calendar sending/contacts search for everyone until reconnected.
 */
export function useDisconnectGoogleAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/google/disconnect", { method: "POST" });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Failed to disconnect Google account");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: googleConnectionKeys.status });
      toast.success("Google account disconnected");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to disconnect Google account");
    },
  });
}
